import { Box, Button, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { alpha, useTheme } from '@mui/material/styles';

import GlobalDialog from '../../components/@extended/GlobalDialog';
import LoadingButton from '../../components/@extended/LoadingButton';
import { CopierAddTradingHoursDialogProps } from '../../types/copier';
import { ReactComponent as ArrowDropDownIcon } from '../../assets/images/icons/angle-down.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check.svg';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash6.svg';
import useAuth from '../../hooks/useAuth';
import { useTradeClient } from '../../client/client-hook';
import { useDispatch } from '../../store';
import { DispatchSnackbarService } from '../../services';
import { UpdateSlaveReq } from '../../proto/trade-copier_pb';
import { filter_type_map_value, risk_type_map_value } from './CopyTradingTableSection';
import {
  DAY_LABELS,
  LocalTradingHour,
  dayLabel,
  dayNumber,
  localToUTCTradingHours,
  utcToLocalTradingHours,
  validateLocalTradingHours
} from '../../utils/trading-hours';

const service = new DispatchSnackbarService();

const CopierAddTradingHoursDialog = ({ isOpen, onClose, selectedSlave, selectedMaster, onSuccess }: CopierAddTradingHoursDialogProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const days = [...DAY_LABELS];

  type TradingHourRow = {
    day: string;
    startTime: string;
    endTime: string;
  };

  const [tradingHours, setTradingHours] = useState<TradingHourRow[]>([]);

  const toLocalTradingHours = (rows: TradingHourRow[]): LocalTradingHour[] =>
    rows.map((row) => ({ dayOfWeek: dayNumber(row.day), startTime: row.startTime, endTime: row.endTime }));

  const toRows = (hours: LocalTradingHour[]): TradingHourRow[] =>
    hours.map((hour) => ({ day: dayLabel(hour.dayOfWeek), startTime: hour.startTime, endTime: hour.endTime }));

  useEffect(() => {
    if (isOpen && selectedSlave?.slaveData?.tradingHoursList?.length) {
      setTradingHours(toRows(utcToLocalTradingHours(selectedSlave.slaveData.tradingHoursList, user?.timezone || 'UTC')));
    } else if (isOpen) {
      setTradingHours([]);
    }
  }, [isOpen, selectedSlave]);

  const handleSave = async () => {
    if (!user || !user.tcId || !selectedSlave || !selectedSlave.slaveData) {
      service.dispatchSnackbar(dispatch, 'Missing required information', 'alert', 'error', 5000);
      return;
    }

    const localHours = toLocalTradingHours(tradingHours);
    const validationError = validateLocalTradingHours(localHours);
    if (validationError) {
      service.dispatchSnackbar(dispatch, validationError, 'alert', 'error', 5000);
      return;
    }

    try {
      setLoading(true);
      const tradingHoursList = localToUTCTradingHours(localHours, user.timezone || 'UTC');

      const req: UpdateSlaveReq.AsObject = {
        userId: user.tcId,
        configId: selectedSlave.configId || '',
        slaveId: selectedSlave.id || '',
        enable: selectedSlave.slaveData.enabled,
        riskType: risk_type_map_value[selectedSlave.slaveData.riskType?.value || 'UNKNOWN'],
        lotMultiplierValue: selectedSlave.slaveData.lotMultiplierValue,
        fixedLotValue: selectedSlave.slaveData.fixedLotValue,
        copyTakeProfit: selectedSlave.slaveData.copyTakeProfit,
        copyStopLoss: selectedSlave.slaveData.copyStopLoss,
        copyPendingOrders: selectedSlave.slaveData.copyPendingOrders,
        forceMinimumLotSize: selectedSlave.slaveData.forceMinimumLotSize,
        forceMaximumLotSize: selectedSlave.slaveData.forceMaximumLotSize,
        copyComment: selectedSlave.slaveData.copyComment,
        customComment: selectedSlave.slaveData.customComment,
        reverseTrade: selectedSlave.slaveData.reverseTrade,
        strictClose: selectedSlave.slaveData.strictClose,
        considerContractSize: selectedSlave.slaveData.considerContractSize,
        copyMagicNumber: selectedSlave.slaveData.copyMagicNumber,
        magicNumber: selectedSlave.slaveData.magicNumber,
        lotRefinerValue: selectedSlave.slaveData.lotRefinerValue,
        magicFiltersList: selectedSlave.slaveData.magicFiltersList,
        copyExpiryTime: selectedSlave.slaveData.copyExpiryTime,
        commentFiltersList: selectedSlave.slaveData.commentFiltersList,
        minLotFilter: selectedSlave.slaveData.minLotFilter,
        maxLotFilter: selectedSlave.slaveData.maxLotFilter,
        stopLossPip: selectedSlave.slaveData.stopLossPip,
        takeProfitPip: selectedSlave.slaveData.takeProfitPip,
        slRefiner: selectedSlave.slaveData.slRefiner,
        tpRefiner: selectedSlave.slaveData.tpRefiner,
        manageExisting: selectedSlave.slaveData.manageExisting,
        allowedTradeSide: selectedSlave.slaveData.allowedTradeSide,
        copyExistingTrades: selectedSlave.slaveData.copyExistingTrades,
        balanceMultiplierValue: selectedSlave.slaveData.balanceMultiplierValue,
        balancePercentageValue: selectedSlave.slaveData.balancePercentageValue,
        fixedBalanceMultiplierValue: selectedSlave.slaveData.fixedBalanceMultiplierValue,
        fixedMasterBalance: selectedSlave.slaveData.fixedMasterBalance,
        maxDelayTime: selectedSlave.slaveData.maxDelayTime,
        minDelayTime: selectedSlave.slaveData.minDelayTime,
        tradingHoursList: tradingHoursList,
        magicFilterType: filter_type_map_value[selectedSlave.slaveData.magicFilterType?.value || 'UNKNOWN'],
        commentFilterType: filter_type_map_value[selectedSlave.slaveData.commentFilterType?.value || 'UNKNOWN']
      };

      const client = await tradeClient();
      await client.updateSlave(req);

      setLoading(false);
      service.dispatchSnackbar(dispatch, 'Trading hours updated successfully', 'alert', 'success', 5000);

      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      console.error('ERROR: ', err);
      setLoading(false);
      service.dispatchSnackbar(dispatch, err.message || 'Failed to update trading hours', 'alert', 'error', 5000);
    }
  };

  const inputStyle = {
    borderRadius: '6px',
    '& .MuiOutlinedInput-root': {
      boxShadow: 'none !important',
      '& fieldset': {
        border: 'none'
      },
      '&:hover fieldset': {
        border: 'none'
      },
      '&.Mui-focused fieldset': {
        border: 'none'
      },
      '& input': {
        boxShadow: 'none',
        outline: 'none',
        fontWeight: 500,
        fontSize: sizeByBreakpoint(1, 16),
        cursor: 'pointer',
        accentColor: theme.palette.primary.main,
        colorScheme: 'light',
        '&::-webkit-datetime-edit-hour-field:focus': {
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        },
        '&::-webkit-datetime-edit-minute-field:focus': {
          backgroundColor: theme.palette.primary.main,
          color: 'white'
        }
      }
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      fontSize: '18px',
      padding: '4px',
      marginLeft: '4px',
      filter: `opacity(0.7)`
    }
  };

  const formBoxStyle = {
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    px: 1.5,
    py: 0.5,
    cursor: 'pointer',
    height: sizeByBreakpoint(2, 44),
    borderWidth: 1,
    borderColor: theme.palette.border.main + '!important',
    borderRadius: '10px',
    '&:focus-within': {
      borderColor: theme.palette.primary.main
    },
    border: '1px solid',
    backgroundColor: theme.palette.background.paper,
    fontSize: sizeByBreakpoint(1, 16)
  };

  return (
    <GlobalDialog
      open={isOpen}
      onClose={onClose}
      title={`Trading Hours (${user?.timezone || 'UTC'})`}
      subtitle="Showing the list of all the accounts."
      footer={
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <LoadingButton
            sx={{
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: sizeByBreakpoint(1, 16),
              height: sizeByBreakpoint(1, 40)
            }}
            loadingPosition="end"
            startIcon={<ClockCircleOutlined style={{ fontSize: '18px' }} />}
            loading={false}
            disableElevation
            disabled={false}
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setTradingHours([...tradingHours, { day: days[0], startTime: '00:00', endTime: '00:00' }]);
            }}
          >
            Add Trading Hours
          </LoadingButton>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40),
                color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary),
                borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]),
                '&:hover': {
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[500]),
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50])
                }
              }}
              disableElevation
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>

            <LoadingButton
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40)
              }}
              loadingPosition="end"
              startIcon={<CheckIcon />}
              loading={loading}
              disableElevation
              disabled={loading}
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              Confirm
            </LoadingButton>
          </Box>
        </Box>
      }
      maxWidth="md"
    >
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.warning.lighter, 0.4),
          border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}` : 'none',

          p: sizeByBreakpoint(1, 15),
          borderRadius: '10px',
          mb: sizeByBreakpoint(1, 20)
        }}
      >
        <Typography
          variant="body2"
          color={theme.palette.mode === 'dark' ? theme.palette.warning.light : alpha(theme.palette.warning.main, 0.8)}
          sx={{
            fontSize: sizeByBreakpoint(1, 14)
          }}
          fontWeight={500}
        >
          Configure the trading hours that you wish to copy trades, any trades placed outside of this time will be ignored. You can
          configure the same day more than once to have multiple trading times per day.
        </Typography>
      </Box>
      {tradingHours.map((hour, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: sizeByBreakpoint(1, 15)
          }}
        >
          <Box
            sx={{
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: sizeByBreakpoint(1, 15)
            }}
          >
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box sx={formBoxStyle}>
                <CalendarOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Day:
                </Typography>

                <Select
                  value={hour.day}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].day = e.target.value;
                    setTradingHours(newHours);
                  }}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  sx={{
                    padding: '0 !important',
                    height: '100%',
                    fontSize: sizeByBreakpoint(1, 16),
                    fontWeight: 500,
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.MuiOutlinedInput-root': {
                      boxShadow: 'none',
                      outline: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-icon': {
                      marginTop: '-2px'
                    }
                  }}
                >
                  {days.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box
                sx={formBoxStyle}
                onClick={() => {
                  const input = document.querySelector(`#start-time-${index}`) as HTMLInputElement;
                  if (input && 'showPicker' in input) (input as any).showPicker();
                }}
              >
                <ClockCircleOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Time:
                </Typography>

                <TextField
                  id={`start-time-${index}`}
                  sx={inputStyle}
                  type="time"
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    style: {
                      accentColor: theme.palette.primary.main
                    }
                  }}
                  value={hour.startTime}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].startTime = e.target.value;
                    setTradingHours(newHours);
                  }}
                  fullWidth
                />
              </Box>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: '230px' }} variant="outlined" size="small">
              <Box
                sx={formBoxStyle}
                onClick={() => {
                  const input = document.querySelector(`#end-time-${index}`) as HTMLInputElement;
                  if (input && 'showPicker' in input) (input as any).showPicker();
                }}
              >
                <ClockCircleOutlined width="20px" height="20px" color={theme.palette.primary.main} />

                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }} fontSize={textSizeByBreakpoint(16)}>
                  Time:
                </Typography>

                <TextField
                  id={`end-time-${index}`}
                  sx={inputStyle}
                  type="time"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    style: {
                      accentColor: theme.palette.primary.main
                    }
                  }}
                  value={hour.endTime}
                  onChange={(e) => {
                    const newHours = [...tradingHours];
                    newHours[index].endTime = e.target.value;
                    setTradingHours(newHours);
                  }}
                  fullWidth
                />
              </Box>
            </FormControl>
          </Box>
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => {
              const newHours = tradingHours.filter((_, i) => i !== index);
              setTradingHours(newHours);
            }}
            sx={{
              minWidth: 'max-content',
              width: sizeByBreakpoint(2, 44),
              height: sizeByBreakpoint(2, 44),
              fontSize: sizeByBreakpoint(1.5, 18)
            }}
          >
            <Box
              component={TrashIcon}
              sx={{
                fontSize: sizeByBreakpoint(1, 16),
                '& path': {
                  fill: theme.palette.error.main
                }
              }}
            />
          </Button>
        </Box>
      ))}
    </GlobalDialog>
  );
};

export default CopierAddTradingHoursDialog;
